/**
 * AdTaxonomyCanonicalsPanel — Phase 5 Surface 2.
 *
 * Power-curator table over canonical_entities WHERE entity_type =
 * 'ad_taxonomy_node'. Renders the resolved CreativeTemplate / layout_archetype /
 * visual_style canonicals with axis chip rail, maturity filter, search,
 * row selection, and "Merge as variants of..." bulk action.
 *
 * Mounted inside AdTaxonomyCatalogPage as the "Canonicals" tab so the
 * existing Explorer + Proposed tabs keep working.
 */

import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import {
  AlertCircle,
  CheckCircle2,
  GitMerge,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { catalogsApi } from '@/services/api';
import type { AdTaxonomyRow } from '@/services/api';

const AXIS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All axes' },
  { value: 'creative_template', label: 'Creative Templates' },
  { value: 'layout_archetype', label: 'Layout Archetypes' },
  { value: 'visual_style', label: 'Visual Styles' },
];

const MATURITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All maturity' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'validated', label: 'Validated' },
  { value: 'core', label: 'Core' },
  { value: 'deprecated', label: 'Deprecated' },
  { value: 'unset', label: 'Unset' },
];

function MaturityBadge({ stage }: { stage?: string | null }) {
  const cfg: Record<string, string> = {
    candidate: 'bg-amber-100 text-amber-800 border-amber-200',
    validated: 'bg-sky-100 text-sky-800 border-sky-200',
    core: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    deprecated: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  const label = stage || 'unset';
  const cls = cfg[stage || ''] || 'bg-slate-100 text-slate-500 border-slate-200';
  return (
    <span
      className={clsx(
        'inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium uppercase border',
        cls,
      )}
    >
      {label}
    </span>
  );
}

function MergeDialog({
  selectedRows,
  onClose,
  onMerged,
}: {
  selectedRows: AdTaxonomyRow[];
  onClose: () => void;
  onMerged: () => void;
}) {
  const [targetId, setTargetId] = useState<string>(
    selectedRows[0]?.canonical_id ?? '',
  );
  const [newName, setNewName] = useState<string>('');
  const [mergeKind, setMergeKind] = useState<'merge' | 'variant_of'>('merge');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function execute() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await catalogsApi.mergeAdTaxonomy({
        source_canonical_ids: selectedRows.map((r) => r.canonical_id),
        target_canonical_id: targetId,
        new_canonical_name: newName || undefined,
        merge_kind: mergeKind,
      });
      onMerged();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl border border-slate-200 w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <GitMerge size={16} className="text-brand-teal" />
            <h3 className="text-base font-semibold text-slate-900">
              Merge canonicals
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        </div>

        <div className="text-xs text-slate-500 mb-4">
          {selectedRows.length} canonicals selected. Choose a target and merge
          mode.
        </div>

        <div className="flex flex-col gap-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Merge mode
            </label>
            <div className="flex gap-2">
              {([
                ['merge', 'Merge into one'],
                ['variant_of', 'Link as variants'],
              ] as const).map(([v, lbl]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMergeKind(v)}
                  className={clsx(
                    'flex-1 px-3 py-2 rounded-lg border text-xs font-medium',
                    mergeKind === v
                      ? 'border-brand-teal bg-brand-teal/10 text-brand-teal-dark'
                      : 'border-slate-200 bg-white text-slate-600',
                  )}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Target canonical
            </label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200"
            >
              {selectedRows.map((r) => (
                <option key={r.canonical_id} value={r.canonical_id}>
                  {r.display_name} ({r.variant_count} variants)
                </option>
              ))}
            </select>
          </div>

          {mergeKind === 'merge' && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                New canonical_name (optional)
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="leave empty to keep target name"
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 font-mono"
              />
            </div>
          )}
        </div>

        {err && (
          <div className="mt-3 text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center gap-2">
            <AlertCircle size={12} />
            {err}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={execute}
            disabled={busy}
            className="flex-1 px-3 py-2 rounded-lg bg-navy-700 text-white text-sm font-medium hover:bg-navy-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {mergeKind === 'merge' ? 'Merge canonicals' : 'Link as variants'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdTaxonomyCanonicalsPanel() {
  const [rows, setRows] = useState<AdTaxonomyRow[]>([]);
  const [counts, setCounts] = useState<{
    by_axis: Record<string, number>;
    by_maturity: Record<string, number>;
  }>({ by_axis: {}, by_maturity: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [axis, setAxis] = useState('');
  const [maturity, setMaturity] = useState('');
  const [search, setSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showMerge, setShowMerge] = useState(false);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await catalogsApi.listAdTaxonomy({
        axis: axis || undefined,
        maturity: maturity || undefined,
        search: search || undefined,
        limit: 1000,
      });
      setRows(r.rows);
      setCounts({ by_axis: r.by_axis, by_maturity: r.by_maturity });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axis, maturity]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.canonical_name.toLowerCase().includes(q) ||
        r.display_name.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.canonical_id)),
    [rows, selectedIds],
  );

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  return (
    <div>
      {/* Axis chip rail */}
      <div className="flex items-center gap-1 mb-3 flex-wrap">
        {AXIS_OPTIONS.map((opt) => {
          const count = opt.value === '' ? rows.length : counts.by_axis[opt.value] ?? 0;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setAxis(opt.value)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium',
                axis === opt.value
                  ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark shadow-sm'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {opt.label}
              <span
                className={clsx(
                  'tabular-nums',
                  axis === opt.value ? 'opacity-80' : 'text-slate-400',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search canonical_name or display_name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200"
          />
        </div>
        <select
          value={maturity}
          onChange={(e) => setMaturity(e.target.value)}
          className="text-sm rounded-lg border border-slate-200 px-3 py-2"
        >
          {MATURITY_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        {selectedIds.size >= 2 && (
          <>
            <button
              type="button"
              onClick={() => setShowMerge(true)}
              className="px-3 py-2 rounded-lg bg-navy-700 text-white text-xs font-medium hover:bg-navy-800 flex items-center gap-1"
            >
              <GitMerge size={12} />
              Merge {selectedIds.size}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="px-3 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
            >
              Clear
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">
          <AlertCircle size={15} />
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading…
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
          <CheckCircle2 size={24} />
          <span>No canonicals match the filters.</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-8" />
                <th className="text-left px-3 py-2 font-semibold">Canonical</th>
                <th className="text-left px-3 py-2 font-semibold">Axis</th>
                <th className="text-left px-3 py-2 font-semibold">Layout</th>
                <th className="text-left px-3 py-2 font-semibold">Macro</th>
                <th className="text-left px-3 py-2 font-semibold">Maturity</th>
                <th className="text-right px-3 py-2 font-semibold">Variants</th>
                <th className="text-right px-3 py-2 font-semibold">Clarity</th>
                <th className="text-right px-3 py-2 font-semibold">X-cat</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const sel = selectedIds.has(r.canonical_id);
                return (
                  <tr
                    key={r.canonical_id}
                    className={clsx(
                      'border-b border-slate-100 hover:bg-slate-50',
                      sel && 'bg-brand-teal/5',
                    )}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => toggleSelect(r.canonical_id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900 font-medium leading-tight">
                        {r.display_name}
                      </div>
                      <div className="text-[10px] font-mono text-slate-500 truncate max-w-xs">
                        {r.canonical_name}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] bg-slate-100 text-slate-700 border border-slate-200">
                        {r.axis_slug}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-700">
                      {r.layout_archetype || '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {r.macro_category || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <MaturityBadge stage={r.maturity_stage} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {r.variant_count}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {Math.round((r.composition_clarity || 0) * 100)}%
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {r.cross_catalog_count}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showMerge && (
        <MergeDialog
          selectedRows={selectedRows}
          onClose={() => setShowMerge(false)}
          onMerged={() => {
            clearSelection();
            void reload();
          }}
        />
      )}
    </div>
  );
}

export default AdTaxonomyCanonicalsPanel;
